import { Component, OnInit, NgZone, Renderer2 } from '@angular/core'
import { Router, ActivatedRoute, NavigationEnd, RouterEvent } from '@angular/router'
import { BaseComponent } from 'src/components/base.component'
import { AnalyticsActions } from 'src/actions/analytics-actions'
import { UserActions } from 'src/actions/user-actions'
import { UserQueries, UserForTracking } from 'src/graphql/queries/user.queries'
import { initializePendoForCustomerAndWhiteLabel, initializePendoForWriter } from '../../lib/pendo'
import { updateIntercomSettings } from '../../lib/intercom'
import { paramsErrorToast, paramsSuccessToast, couponToast, paramsInfoToast } from '../../toaster'
import { decodeUrlParam, detectMouseOrKeyboard } from '../../utils'
import { filter } from 'rxjs/operators'
import { VersionCheckService } from '../../services/version-check.service'
import { setTheme } from 'ngx-bootstrap/utils'
import { environment } from 'src/environments/environment'
import { omit, throttle } from 'lodash-es'
import { MomentLocaleService } from 'src/services/moment-locale.service'
import { UserMutations } from 'src/graphql/mutations/user.mutations'

const fiveMinutes = 5 * 60 * 1000

@Component({
  selector: 'main-app',
  template: `
    <tos-modal
      [role]="this.user.role"
      [submitting]="submitting"
      *ngIf="user && user.showTosMessage && !user.adminImpersonating"
      (submit)="agreeToTos()">
    </tos-modal>
    <router-outlet></router-outlet>

  `
})
export class MainApp extends BaseComponent implements OnInit {
  user: UserForTracking
  submitting = false
  private refreshTimer: any

  constructor(public userActions: UserActions,
  private analyticsActions: AnalyticsActions,
  private userQueries: UserQueries,
  private router: Router,
  private route: ActivatedRoute,
  private renderer: Renderer2,
  private versionCheckService: VersionCheckService,
  private zone: NgZone,
  private momentLocaleService: MomentLocaleService,
  private userMutations: UserMutations
  ) {
    super()
  }

  ngOnInit(): void {
    detectMouseOrKeyboard()
    setTheme('bs4') // or 'bs3'
    this.momentLocaleService.init()
    if (environment.production) this.versionCheckService.initVersionCheck()

    const userQuery = this.userQueries.userForTracking()
    userQuery.valueChanges.subscribe(({ data, loading }) => {
      if (!loading && this.user !== data.currentUser) {
        this.user = data.currentUser
        if (!this.user || (this.user.role === 'customer' && !this.user.adminImpersonating)) {
          this.analyticsActions.setUser(this.user)
        }
      }
    })
    const throttledUserQuery = throttle(() => userQuery.refetch(), fiveMinutes)

    const routerObs = this.router.events.pipe(filter((event: RouterEvent) => event instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
      throttledUserQuery()
      document.body.scrollTop = 0
      document.documentElement.scrollTop = 0
      if (this.user && !this.user.adminImpersonating && ['customer', 'white_label'].includes(this.user.role)) {
        initializePendoForCustomerAndWhiteLabel(e.url)
      }
      if (this.user && !this.user.adminImpersonating && this.user.role === 'writer') {
        initializePendoForWriter(e.url)
      }
      if (this.user && !this.user.adminImpersonating && this.user.role === 'customer') {
        updateIntercomSettings(this.user)
      }
    })

    const routeObs = this.route.queryParams.subscribe(queryParams => {
      const success = decodeUrlParam(queryParams['success'])
      if (success) paramsSuccessToast(success)

      const err = decodeUrlParam(queryParams['error'])
      if (err) paramsErrorToast(err)

      const info = decodeUrlParam(queryParams['info'])
      if (info) paramsInfoToast(info)

      const coupon = decodeUrlParam(queryParams['coupon'])
      if (coupon) {
        couponToast(coupon)
      }

      // Clear query params messaging
      if (success || err || info || coupon) {
        const currentQueryParams = this.route.snapshot.queryParams
        this.router.navigate([], {relativeTo: this.route, queryParams: omit(currentQueryParams, 'success', 'error', 'info', 'coupon')})
      }
    })

    this.observers.push(routerObs, routeObs)
    this.resetTimer()
    this.renderer.listen('document', 'mousemove',
      // Calls resetTimer immediately
      throttle(() => this.resetTimer(), fiveMinutes)
    )
  }

  agreeToTos(): void {
    this.submitting = true
    this.userMutations.userAcceptTos(true).then(() => {
      this.user = Object.assign({}, this.user, {showTosMessage: false})
      this.submitting = false
    })
  }

  // Refreshes the page after 8 hours of inactivity
  resetTimer(): void {
    clearTimeout(this.refreshTimer)
    this.refreshTimer = setTimeout(() => {
      if (navigator.onLine) {
        this.zone.runOutsideAngular(() => location.reload())
      }
    }, 28800000) // 8 hours
  }
};
